import getSymbolFromCurrency from 'currency-symbol-map';
import { KeyedMutator } from 'swr';

import {
  BulkActionTransactionsParams,
  BulkExportTransactionsRequest,
  Destination,
  ExportDataDeliveryType,
  MANUAL_UPLOAD_SOURCE_PLATFORM,
  Marketplace,
  SortState,
  TransactionCreationMethod,
  TransactionFilterAllowedOptionsResponse,
  TransactionListItem,
  TransactionStatus,
  TransactionType,
} from '@commenda/api/new-sales-tax/transactions/types';
import { TSetNotification } from '@commenda/types/src/random';

import { bulkDeleteTransactions, bulkExportTransactions } from '@commenda/api';
import { TransactionSourcePlatformText } from '@commenda/api/sales-tax/constants';
import { MarketplaceNameMapping } from '@commenda/api/sales-tax/types';
import { getClientInfoFromCookie } from '@commenda/auth';
import { FeatureLaunch } from '@commenda/feature-launches/featureLaunchEnum';
import { isFeatureLaunched } from '@commenda/feature-launches/isFeatureLaunched';
import {
  Badge,
  Button,
  DataTable,
  FilterConfig as DataTableFilterConfig,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import {
  compareDatesChronologically,
  getAllCountryOptions,
  NonTimezoneSensitiveDate,
} from '@commenda/utils';
import { RootFiIntegration } from '@prisma/client';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_DATE_OPTIONS } from '../common/filters/DateFilter/constants';
import { FilterChangeEvent } from '../common/filters/types';
import SalesTaxSectionLayout from '../common/section-layout';
import { useTableRowSelectionHandler } from '../common/utils/useTableRowSelectionHandler';
import {
  formatNumberWithCommasAndDecimals,
  getCountryName,
  getStateName,
  getTransactionStatusBadgeColor,
  getTransactionTypeOfSalesBadgeColor,
  SALES_TAX_STATUS_TO_TEXT,
  TRANSACTION_SALE_ENUM_TO_TEXT,
} from '../utils';
import TransactionDeleteModal from './modal/TransactionDeleteModal';
import {
  getAllStatesOptions,
  SalesTaxFiltersType,
} from './TransactionFilters/config';
import { TransactionRowActionMenu } from './TransactionRowActionMenu';

// Map column IDs to filter columnIds for showing/hiding filters
const COLUMN_TO_FILTER_MAP: Record<string, string> = {
  date: 'date',
  reportingDate: 'reporting_date',
  type: SalesTaxFiltersType.TYPE,
  destinationState: SalesTaxFiltersType.DESTINATION_STATES,
  sourcePlatform: SalesTaxFiltersType.SOURCE_PLATFORMS,
  status: SalesTaxFiltersType.STATUSES,
  marketplace: SalesTaxFiltersType.MARKETPLACES,
};

interface TransactionsTableSectionProps {
  corporationId: number;
  data: TransactionListItem[];
  filters: Record<string, any>;
  totalTransactions: number;
  nextCursor: string | null;
  currentCursor: string | null;
  onPageChange: (cursor: string | null) => void;
  onSuccess: KeyedMutator<any>;
  searchTerm: string;
  handleSearch: (searchValue: string) => void;
  sortState?: SortState;
  onSortChange: (sortState: SortState) => void;
  pageSize: number;
  setNotification: TSetNotification;
  tableContainerClassName?: string;
  tableWrapperClassName?: string;
  showEstimatedTaxBreakdown?: boolean;
  filterOptions?: TransactionFilterAllowedOptionsResponse;
  onFilterChange?: (event: FilterChangeEvent) => void;
  onFilterClear?: (filterId: string) => void;
  onClearAll?: () => void;
  salesTaxTestMode?: boolean;
  defaultDateColumn?: 'date' | 'reportingDate';
  storageKeySuffix?: string;
  /** ISO 3166-1 country code for the corporation's incorporation country */
  incorporationCountry?: string;
  /** Whether to use the EU/UK upload flow (simplified flow without template selection) */
  isEuUploadFlow?: boolean;
}

const TransactionsTableSection = ({
  corporationId,
  data,
  filters,
  totalTransactions,
  nextCursor,
  currentCursor,
  onPageChange,
  onSuccess,
  pageSize,
  searchTerm,
  handleSearch,
  sortState,
  onSortChange,
  setNotification,
  tableContainerClassName,
  tableWrapperClassName,
  showEstimatedTaxBreakdown,
  filterOptions,
  onFilterChange,
  onFilterClear,
  onClearAll,
  salesTaxTestMode = false,
  defaultDateColumn = 'date',
  storageKeySuffix,
  incorporationCountry,
  isEuUploadFlow = false,
}: TransactionsTableSectionProps) => {
  const router = useRouter();
  const clientInfo = getClientInfoFromCookie();
  const user = clientInfo?.user;
  const [enableInternalTools, setEnableInternalTools] = useState(false);

  const FIRST_PAGE_CURSOR = 'FIRST_PAGE_CURSOR';

  useEffect(() => {
    if (router.query) {
      const { showInternalTools } = router.query;
      setEnableInternalTools(showInternalTools === 'true');
    }
  }, [router.query]);

  const deleteModalVisibility = useToggle();

  const defaultVisibleColumns = [
    'type',
    'status',
    'customerName',
    'destinationState',
    'destinationCountry',
    defaultDateColumn,
    'amount',
    'taxCollected',
    'total',
    'sourcePlatform',
    'sourcePlatformId',
    'invoiceNumber',
    'discount',
  ];

  const SALES_TAX_TRANSACTIONS_TABLE_COLUMN_GROUP_STORAGE_KEY = `sales-tax-transactions-table-column-group-corporation-${corporationId}${storageKeySuffix ? `-${storageKeySuffix}` : ''}`;

  const transactionSelection = useTableRowSelectionHandler({
    getItemId: (item: TransactionListItem): string => item.id,
    totalItems: totalTransactions,
  });

  /**
   * normalizeDestinations
   *
   * Rules
   * 1) If ANY destination includes `states` (i.e., user is filtering at state-level),
   *   then ONLY keep destinations that also include `states`.
   *   Country-only destinations are rejected.
   * 2) Trim/normalize country + state codes, drop empties.
   * 3) Deduplicate exact filters (same country + same states set).
   * 4) Merge multiple state filters for the same country into one (union of states).
   *
   * Examples:
   * - [{US}, {US, ["TX"]}] -> [{US, ["TX"]}]              (country-only rejected)
   * - [{US}, {CA}] -> [{US}, {CA}]                       (no state-level present, keep as-is)
   * - [{US, ["TX"]}, {CA}] -> [{US, ["TX"]}]             (CA rejected because it's country-only)
   * - [{US, ["TX"]}, {US, ["CA"]}] -> [{US, ["CA","TX"]}]
   */
  function normalizeDestinations(destinations: Destination[]): Destination[] {
    // 1) Clean inputs (trim, drop empty country/state entries)
    const normalizedInputs: Destination[] = destinations
      .map((destination) => {
        const country = destination.country.trim();

        const states = destination.states
          ?.map((state) => state.trim())
          .filter((state) => state.length > 0);

        return {
          country,
          states: states && states.length > 0 ? states : undefined,
        };
      })
      .filter((destination) => destination.country.length > 0);

    // 2) If ANY filter is state-level, reject all country-only filters
    const hasAnyStateLevelFilter = normalizedInputs.some(
      (destination) => destination.states && destination.states.length > 0
    );

    const inputsToProcess = hasAnyStateLevelFilter
      ? normalizedInputs.filter(
          (destination) => destination.states && destination.states.length > 0
        )
      : normalizedInputs;

    // 3) Aggregate by country (union states)
    const aggregatedByCountry = new Map<string, Set<string>>();

    for (const destination of inputsToProcess) {
      // When state-level is enforced, destination.states is always present
      if (!destination.states) continue;

      const selectedStates =
        aggregatedByCountry.get(destination.country) ?? new Set<string>();
      for (const state of destination.states) {
        selectedStates.add(state);
      }
      aggregatedByCountry.set(destination.country, selectedStates);
    }

    // 4) Build final list
    const normalizedDestinations: Destination[] = [];

    if (hasAnyStateLevelFilter) {
      // Only state-level results
      for (const [country, selectedStates] of Array.from(
        aggregatedByCountry.entries()
      )) {
        normalizedDestinations.push({
          country,
          states: Array.from(selectedStates).sort(),
        });
      }
      return normalizedDestinations;
    }

    // No state-level filters at all -> keep country-only filters, dedup by country
    const seenCountries = new Set<string>();
    for (const destination of inputsToProcess) {
      if (!destination.states) {
        if (!seenCountries.has(destination.country)) {
          normalizedDestinations.push({ country: destination.country });
          seenCountries.add(destination.country);
        }
      } else {
        const selectedStates = aggregatedByCountry.get(destination.country);
        if (selectedStates) {
          normalizedDestinations.push({
            country: destination.country,
            states: Array.from(selectedStates).sort(),
          });
          aggregatedByCountry.delete(destination.country); // avoid duplicates
        }
      }
    }

    return normalizedDestinations;
  }

  const getTransactionSelectionPayload = (): BulkActionTransactionsParams => {
    const DEFAULT_START_DATE = '1970-01-01';
    const DEFAULT_END_DATE = new Date().toISOString().split('T')[0]; // today

    const {
      corporationId: corpIdFromFilters,
      currency,
      ...restFilters
    } = filters;

    const selectedRowsId = Array.from(transactionSelection.selectedItemIds);
    const unselectedRowsId = Array.from(transactionSelection.unselectedItemIds);

    if (selectedRowsId.length > 0) {
      return {
        corporation_id: corporationId,
        transaction_ids: selectedRowsId,
      };
    }

    const defaultDateRange =
      Object.keys(restFilters).length === 0 && selectedRowsId.length === 0
        ? {
            from_date: DEFAULT_START_DATE,
            to_date: DEFAULT_END_DATE,
          }
        : {};

    const shipToCountries: Destination[] | undefined =
      restFilters.destination_countries
        ? restFilters.destination_countries.map((country) => ({
            country,
          }))
        : undefined;

    const shipToAddresses: Destination[] | undefined =
      restFilters.destination_states
        ? restFilters.destination_states.map((state) => ({
            country: state.split('_')[0],
            states: [state.split('_')[1]],
          }))
        : undefined;

    const transactionsCreationMethods: TransactionCreationMethod[] | undefined =
      restFilters.creation_methods
        ? Array.isArray(restFilters.creation_methods)
          ? restFilters.creation_methods
          : [restFilters.creation_methods]
        : undefined;

    const sourcePlatforms:
      | (RootFiIntegration | typeof MANUAL_UPLOAD_SOURCE_PLATFORM)[]
      | undefined = restFilters.source_platforms
      ? Array.isArray(restFilters.source_platforms)
        ? restFilters.source_platforms
        : [restFilters.source_platforms]
      : undefined;

    const marketplaces: Marketplace[] | undefined = restFilters.marketplaces
      ? Array.isArray(restFilters.marketplaces)
        ? restFilters.marketplaces
        : [restFilters.marketplaces]
      : undefined;

    return {
      corporation_id: corporationId,
      excluded_ids: unselectedRowsId,
      filters: {
        ...defaultDateRange,
        ...((shipToAddresses || shipToCountries) && {
          ship_to_addresses: [
            ...normalizeDestinations([
              ...(shipToCountries || []),
              ...(shipToAddresses || []),
            ]),
          ],
        }),
        ...(restFilters.from_date && { from_date: restFilters.from_date }),
        ...(restFilters.to_date && { to_date: restFilters.to_date }),
        ...(restFilters.reporting_date_override_from_date && {
          reporting_date_override_from_date:
            restFilters.reporting_date_override_from_date,
        }),
        ...(restFilters.reporting_date_override_to_date && {
          reporting_date_override_to_date:
            restFilters.reporting_date_override_to_date,
        }),
        // ...(currencies && { currencies }),
        ...(transactionsCreationMethods && {
          transaction_creation_methods: transactionsCreationMethods,
        }),
        ...(sourcePlatforms && {
          source_platforms: sourcePlatforms,
        }),
        ...(marketplaces && {
          marketplaces,
        }),
        search_term: searchTerm,
        ...(restFilters.statuses &&
          restFilters.statuses.length > 0 && {
            statuses: restFilters.statuses,
          }),
        ...(restFilters.type && { type: restFilters.type }),
      },
    };
  };

  const transactionsData = {
    // if transaction is refund, then amount should be negative
    transactions: data.map((item) => {
      const isRefund =
        item.type === TransactionType.REFUND ||
        item.type === TransactionType.PURCHASE_REFUND;
      return {
        ...item,
        taxCollected: isRefund
          ? -Math.abs(item.taxCollected)
          : item.taxCollected,
        total: isRefund ? -Math.abs(item.total) : item.total,
        subTotal: isRefund ? -Math.abs(item.subTotal) : item.subTotal,
        discount: isRefund ? -Math.abs(item.discount) : item.discount,
        shipping_and_handling: isRefund
          ? -Math.abs(item.shipping_and_handling)
          : item.shipping_and_handling,
        amount: isRefund ? -Math.abs(item.subTotal) : item.subTotal,
      };
    }),
    totalTransactions,
    nextCursor,
  };

  const handleNextPage = () => {
    if (nextCursor) {
      onPageChange(nextCursor);
    }
  };

  const handlePreviousPage = () => {
    onPageChange(currentCursor);
  };

  const onTransactionIdCopyClick = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);

      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: 'Transaction ID copied to clipboard',
      });
    } catch (err) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to copy transaction ID',
        message: 'Failed to copy transaction ID. Please try again',
      });
    }
  };

  const onCustomerEmailCopyClick = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);

      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: 'Customer email copied to clipboard',
      });
    } catch (err) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to copy customer email',
        message: 'Failed to copy customer email. Please try again',
      });
    }
  };

  useEffect(() => {
    transactionSelection.resetSelection();
  }, [searchTerm, filters]);

  const columnGroups = [
    {
      id: 'id',
      sticky: 'left' as const,
      columns: [
        {
          accessorKey: 'id',
          header: 'Transaction ID',
          title: 'Transaction ID',
          cell: ({ row }) => {
            const id = row.getValue('id');
            return (
              <div className="flex items-center group relative w-full">
                <span>{id}</span>

                <button
                  type="button"
                  className="opacity-0 group-hover:opacity-100 transition-opacity absolute right-1 p-1 bg-white rounded-sm shadow-sm hover:shadow-md"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTransactionIdCopyClick(id);
                  }}
                >
                  <Icons.Copy01 className="text-text-tertiary scale-75" />
                </button>
              </div>
            );
          },
          enableSorting: false,
        },
      ],
    },
    {
      id: 'type',
      columns: [
        {
          accessorKey: 'type',
          header: 'Type',
          title: 'Type of sale',
          cell: ({ row }) => {
            // Use metadata.transactionType if present, otherwise fall back to type
            const typeOfSale = (row.original?.metadata?.transactionType ||
              row.getValue('type')) as TransactionType;
            return (
              <Badge variant={getTransactionTypeOfSalesBadgeColor(typeOfSale)}>
                {TRANSACTION_SALE_ENUM_TO_TEXT[typeOfSale]}
              </Badge>
            );
          },
          enableSorting: false,
        },
      ],
    },
    {
      id: 'status',
      columns: [
        {
          accessorKey: 'status',
          header: 'Status',
          title: 'Status',
          cell: ({ row }) => {
            const status = row.getValue('status') as TransactionStatus;
            return (
              <Badge variant={getTransactionStatusBadgeColor(status)}>
                {SALES_TAX_STATUS_TO_TEXT[status]}
              </Badge>
            );
          },
          enableSorting: false,
        },
      ],
    },
    // VAT Number column - only shown for DE/GB corporations
    ...(incorporationCountry === 'DE' || incorporationCountry === 'GB'
      ? [
          {
            id: 'VAT Number',
            columns: [
              {
                accessorKey: 'vatNumber',
                header: 'VAT Number',
                title: 'VAT Number',
                cell: ({ row }) => {
                  const vatNumber = row?.original?.metadata?.vatNumber;
                  return (
                    <div className="flex items-center group relative w-full">
                      <span>{vatNumber}</span>
                    </div>
                  );
                },
                enableSorting: false,
              },
            ],
          },
        ]
      : []),
    {
      id: 'customer',
      columns: [
        {
          id: 'name',
          accessorFn: (row) => row?.customer?.name,
          accessorKey: 'name',
          header: 'Customer',
          title: 'Customer',
          cell: ({ row }) => {
            const customerName = row.original?.customer?.name as string;
            return customerName ? (
              <Button
                variant="link"
                className="txt-small-regular text-text-primary truncate max-w-48 justify-start"
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.original?.customer?.id) {
                    router.push(
                      `/sales-tax/customer/${row.original?.customer?.id}?corporationId=${corporationId}`
                    );
                  }
                }}
              >
                {customerName}
              </Button>
            ) : (
              <span className="txt-small-regular text-text-primary">–</span>
            );
          },
          enableSorting: false,
        },
        {
          id: 'email',
          accessorFn: (row) => row?.customer?.email,
          accessorKey: 'email',
          header: 'Customer Email',
          title: 'Customer Email',
          cell: ({ row }) => {
            const customerEmail = row.original?.customer?.email as string;
            return customerEmail ? (
              <div className="flex items-center group relative w-full">
                <span className="txt-small-regular text-text-primary truncate max-w-96">
                  {customerEmail || '-'}
                </span>

                <button
                  type="button"
                  className="opacity-0 group-hover:opacity-100 transition-opacity absolute right-1 p-1 bg-white rounded-sm shadow-sm hover:shadow-md"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCustomerEmailCopyClick(customerEmail);
                  }}
                >
                  <Icons.Copy01 className="text-text-tertiary scale-75" />
                </button>
              </div>
            ) : (
              <span className="txt-small-regular text-text-primary">–</span>
            );
          },
          enableSorting: false,
        },
      ],
    },
    {
      id: 'destinationState',
      columns: [
        {
          accessorKey: 'destinationState',
          accessorFn: (row) =>
            row.destinationState
              ? getStateName(row.destinationState, row.destinationCountry)
              : '—',
          header: 'Ship-to State',
          title: 'Ship-to State',
          cell: ({ row }) => {
            const destinationState = row.getValue('destinationState') as string;
            const destinationCountry = row.original
              .destinationCountry as string;
            return (
              <p className="txt-small-regular text-text-primary">
                {destinationState
                  ? getStateName(destinationState, destinationCountry)
                  : '—'}
              </p>
            );
          },
          enableSorting: false,
        },
      ],
    },
    {
      id: 'destinationCountry',
      columns: [
        {
          accessorKey: 'destinationCountry',
          accessorFn: (row) =>
            row.destinationCountry
              ? getCountryName(row.destinationCountry)
              : '—',
          header: 'Ship-to Country',
          title: 'Ship-to Country',
          cell: ({ row }) => {
            const destinationCountry = row.original
              .destinationCountry as string;
            return (
              <p className="txt-small-regular text-text-primary">
                {destinationCountry ? getCountryName(destinationCountry) : '—'}
              </p>
            );
          },
          enableSorting: false,
        },
      ],
    },
    {
      id: 'date',
      columns: [
        {
          accessorKey: 'date',
          header: 'Transaction Date',
          title: 'Transaction Date',
          cell: ({ row }) => {
            return (
              <p className="txt-small-regular text-text-tertiary">
                {row.getValue('date')}
              </p>
            );
          },
          enableSorting: true,
          sort: {
            shouldSort: true,
            sortingFn: compareDatesChronologically,
            ui: {
              asc: {
                columnNode: 'Oldest to Newest',
                globalNode: 'Transaction Date - Oldest to Newest',
              },
              desc: {
                columnNode: 'Newest to Oldest',
                globalNode: 'Transaction Date - Newest to Oldest',
              },
            },
          },
        },
      ],
    },
    {
      id: 'reportingDate',
      columns: [
        {
          accessorKey: 'reportingDate',
          header: 'Reporting Date',
          title: 'Reporting Date',
          cell: ({ row }) => {
            const reportingDate = row.getValue('reportingDate') as string;
            if (!reportingDate) {
              return <Badge variant="gray">Missing</Badge>;
            }
            return (
              <p className="txt-small-regular text-text-tertiary">
                {reportingDate}
              </p>
            );
          },
          enableSorting: true,
          sort: {
            shouldSort: true,
            sortingFn: compareDatesChronologically,
            ui: {
              asc: {
                columnNode: 'Oldest to Newest',
                globalNode: 'Reporting Date - Oldest to Newest',
              },
              desc: {
                columnNode: 'Newest to Oldest',
                globalNode: 'Reporting Date - Newest to Oldest',
              },
            },
          },
        },
      ],
    },
    {
      id: 'marketplace',
      columns: [
        {
          accessorKey: 'marketplace',
          header: 'Marketplace',
          title: 'Marketplace',
          cell: ({ row }) => {
            const value = row.getValue('marketplace');
            if (!value) return <Badge variant="gray">—</Badge>;
            return (
              <Badge variant="gray">
                {MarketplaceNameMapping[value]
                  ? MarketplaceNameMapping[value]
                  : value}
              </Badge>
            );
          },
          enableSorting: false,
        },
      ],
    },
    {
      id: 'sourcePlatform',
      columns: [
        {
          accessorKey: 'sourcePlatform',
          header: 'Source',
          title: 'Source',
          cell: ({ row }) => {
            const value = row.getValue('sourcePlatform');
            if (!value) return <Badge variant="gray">Manual upload</Badge>;
            return (
              <Badge variant="gray">
                {TransactionSourcePlatformText[value]}
              </Badge>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: 'sourcePlatformId',
          header: 'Source Platform ID',
          title: 'Source Platform ID',
          cell: ({ row }) => {
            const value = row.getValue('sourcePlatformId');
            if (!value) return <Badge variant="gray">N/A</Badge>;
            return <Badge variant="gray">{value}</Badge>;
          },
          enableSorting: false,
        },
        {
          accessorKey: 'invoiceNumber',
          header: 'Invoice Number',
          title: 'Invoice Number',
          cell: ({ row }) => {
            const invoiceNumber =
              row.getValue('invoiceNumber') || ('—' as string);
            return <Badge variant="gray">{invoiceNumber}</Badge>;
          },
          enableSorting: false,
        },
      ],
    },
    {
      id: 'amount',
      columns: [
        {
          accessorKey: 'amount',
          header: 'Amount',
          title: 'Amount',
          cell: ({ row }) => {
            const subTotal = row.original.subTotal || 0;
            return (
              <div className="flex justify-end items-center w-full">
                <span className="txt-small-regular text-text-tertiary mr-0.5">
                  {getSymbolFromCurrency(row.original.invoiceCurrency)}
                </span>
                {formatNumberWithCommasAndDecimals(subTotal)}
              </div>
            );
          },
          enableSorting: true,
          sort: {
            shouldSort: true,
            sortingFn: (a: number, b: number) => (a || 0) - (b || 0),
            ui: {
              asc: {
                columnNode: 'Low to High',
                globalNode: 'Amount - Low to High',
              },
              desc: {
                columnNode: 'High to Low',
                globalNode: 'Amount - High to Low',
              },
            },
          },
        },
        {
          accessorKey: 'taxCollected',
          header: 'Tax Collected',
          title: 'Tax Collected',
          cell: ({ row }) => {
            return (
              <div className="flex justify-end items-center w-full">
                <span className="txt-small-regular text-text-tertiary mr-0.5">
                  {getSymbolFromCurrency(row.original.invoiceCurrency)}
                </span>
                {formatNumberWithCommasAndDecimals(
                  row.getValue('taxCollected')
                ) || 0}
              </div>
            );
          },
          enableSorting: true,
          sort: {
            shouldSort: true,
            sortingFn: (a: number, b: number) => (a || 0) - (b || 0),
            ui: {
              asc: {
                columnNode: 'Low to High',
                globalNode: 'Tax collected - Low to High',
              },
              desc: {
                columnNode: 'High to Low',
                globalNode: 'Tax collected - High to Low',
              },
            },
          },
        },
        {
          accessorKey: 'total',
          header: 'Total',
          title: 'Total',
          cell: ({ row }) => {
            return (
              <div className="flex justify-end items-center w-full">
                <span className="txt-small-medium text-text-tertiary mr-0.5">
                  {getSymbolFromCurrency(row.original.invoiceCurrency)}
                </span>

                {formatNumberWithCommasAndDecimals(row.getValue('total')) || 0}
              </div>
            );
          },
          enableSorting: true,
          sort: {
            shouldSort: true,
            sortingFn: (a: number, b: number) => (a || 0) - (b || 0),
            ui: {
              asc: {
                columnNode: 'Low to High',
                globalNode: 'Total - Low to High',
              },
              desc: {
                columnNode: 'High to Low',
                globalNode: 'Total - High to Low',
              },
            },
          },
        },
        {
          accessorKey: 'discount',
          header: 'Discount',
          title: 'Discount',
          cell: ({ row }) => {
            return (
              <div className="flex justify-end items-center w-full">
                <span className="txt-small-regular text-text-tertiary mr-0.5">
                  {getSymbolFromCurrency(row.original.invoiceCurrency)}
                </span>
                {formatNumberWithCommasAndDecimals(row.original?.discount || 0)}
              </div>
            );
          },
          enableSorting: false,
        },
      ],
    },
    {
      id: 'actions',
      sticky: 'right' as const,

      columns: [
        {
          accessorKey: 'actions',
          header: '',
          title: '',
          width: 65,
          showInSelector: false,
          cell: ({ row }) => {
            return (
              <TransactionRowActionMenu
                transaction={row.original}
                corporationId={corporationId}
                refetchTransactions={onSuccess}
                salesTaxTestMode={salesTaxTestMode}
                isInternalUser={enableInternalTools}
              />
            );
          },
          enableSorting: false,
        },
      ],
    },
  ];

  const [visibleSelectedRows, setVisibleSelectedRows] = useState<
    TransactionListItem[]
  >([]);

  // Track visible columns in state so filters can react to changes
  const getInitialVisibleColumns = () => {
    const storageKey = SALES_TAX_TRANSACTIONS_TABLE_COLUMN_GROUP_STORAGE_KEY;
    const storedColumns = localStorage.getItem(storageKey);
    if (!storedColumns) {
      return defaultVisibleColumns;
    }

    try {
      const parsed = JSON.parse(storedColumns);
      // Migrate old format: 'customerName' -> ['name', 'email']
      if (Array.isArray(parsed)) {
        const migrated = parsed.flatMap((col: string) => {
          if (col === 'customerName') {
            return ['name', 'email'];
          }
          return [col];
        });
        return migrated;
      }
      return defaultVisibleColumns;
    } catch {
      return defaultVisibleColumns;
    }
  };

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    getInitialVisibleColumns()
  );

  useEffect(() => {
    const currentPageSelected = data.filter((item) =>
      transactionSelection.isItemSelected(item)
    );
    setVisibleSelectedRows(currentPageSelected);
  }, [
    data,
    transactionSelection.selectedItemIds,
    transactionSelection.unselectedItemIds,
    transactionSelection.isSelectAllActive,
  ]);

  const initialColumns = getInitialVisibleColumns();

  const getEmptyStateConfig = () => {
    const hasActiveFilters =
      transactionsData?.transactions?.length === 0 &&
      Object.keys(filters || {}).some(
        (key) => filters[key] && key !== 'corporationId'
      );

    if (hasActiveFilters) {
      return {
        title: 'No transactions found',
        description: (
          <>
            We couldn&apos;t find any transactions matching your current
            filters. Review your <br />
            selections, or reset filters to view all transactions.
          </>
        ),
      };
    }
    return {
      title: 'No transactions found',
      description: (
        <>
          There are no transactions to display at this time. Try refining <br />
          your search term, adjusting the filters, or reset both to see all
          transactions.
        </>
      ),
    };
  };

  const handleDeleteSelected = async () => {
    const payload = getTransactionSelectionPayload();
    const response = await bulkDeleteTransactions(payload);

    if (response.success) {
      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: 'Transactions deleted successfully',
      });

      onSuccess();
    } else {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to delete transactions',
        message: 'Failed to delete transactions. Please try again',
      });
    }

    deleteModalVisibility.hide();
    transactionSelection.resetSelection();
  };

  const handleExportSelected = async () => {
    if (!user?.email) {
      setNotification({
        show: true,
        valid: false,
        title: 'Export failed',
        message:
          'Unable to export: user email not available. Please try logging in again.',
      });
      return;
    }

    const bulkExportFilters = getTransactionSelectionPayload();
    const delivery = {
      type: ExportDataDeliveryType.EMAIL,
      recipients: [user.email],
    };

    const payload: BulkExportTransactionsRequest = {
      delivery,
      ...bulkExportFilters,
    };

    const response = await bulkExportTransactions(payload);

    if (response.success) {
      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message:
          'Transaction export in progress. You will receive an email with the export once it is ready.',
      });

      onSuccess();
    } else {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to export transactions',
        message: 'Failed to export transactions. Please try again',
      });
    }

    deleteModalVisibility.hide();
    transactionSelection.resetSelection();
  };

  // Convert filters state to DataTable activeFilters format
  const activeFiltersForDataTable = useMemo(() => {
    const activeFilters: Record<string, string | string[]> = {};

    // Handle date period -> map to actual 'date' column for DataTable filtering
    if (filters.from_date && filters.to_date) {
      // Convert from_date/to_date to custom format for DataTable
      activeFilters.date = `custom:${filters.from_date},${filters.to_date}`;
    }

    // Handle reporting date override -> map to 'reporting_date' column for DataTable filtering
    if (
      filters.reporting_date_override_from_date &&
      filters.reporting_date_override_to_date
    ) {
      // Convert reporting_date_override_from_date/to_date to custom format for DataTable
      activeFilters.reporting_date = `custom:${filters.reporting_date_override_from_date},${filters.reporting_date_override_to_date}`;
    }

    // Handle type
    if (filters.type) {
      activeFilters[SalesTaxFiltersType.TYPE] = filters.type;
    }

    // Handle marketplaces
    if (filters.marketplaces) {
      activeFilters[SalesTaxFiltersType.MARKETPLACES] = filters.marketplaces;
    }

    // Handle destination countries
    // Note: Use filter ID to avoid client-side filtering (filtering is server-side)
    if (filters.destination_countries) {
      activeFilters[SalesTaxFiltersType.DESTINATION_COUNTRIES] = Array.isArray(
        filters.destination_countries
      )
        ? filters.destination_countries
        : [filters.destination_countries];
    }

    // Handle destination states
    // Note: Filter values are in format "US_CA" but data has separate destinationCountry and destinationState
    // Since filtering is server-side, we don't need client-side filtering for these
    // But we still need to track them for UI display purposes
    // We'll use a special format to avoid client-side filtering conflicts
    if (filters.destination_states) {
      // Store as internal filter ID to avoid DataTable client-side filtering
      // The actual filtering happens server-side
      activeFilters[SalesTaxFiltersType.DESTINATION_STATES] = Array.isArray(
        filters.destination_states
      )
        ? filters.destination_states
        : [filters.destination_states];
    }

    // Handle source platforms
    if (filters.source_platforms) {
      activeFilters[SalesTaxFiltersType.SOURCE_PLATFORMS] =
        filters.source_platforms;
    }

    // Handle statuses
    if (filters.statuses) {
      activeFilters[SalesTaxFiltersType.STATUSES] = Array.isArray(
        filters.statuses
      )
        ? filters.statuses
        : [filters.statuses];
    }

    return activeFilters;
  }, [filters]);

  // Helper function to calculate date range from DataTable preset
  const calculateDateRangeFromPreset = useCallback(
    (code: string): [string, string] => {
      const now = NonTimezoneSensitiveDate.fromNativeDate(new Date());
      let startDate: NonTimezoneSensitiveDate = now;
      let endDate: NonTimezoneSensitiveDate = now;

      if (/^\d+$/.test(code)) {
        // Last N days
        startDate = now.subtract(parseInt(code, 10), 'days');
        return [startDate.toISO8601String(), endDate.toISO8601String()];
      }

      switch (code) {
        case 'mtd': {
          startDate = now.startOf('month');
          endDate = now;
          break;
        }
        case 'pm': {
          const prevMonth = now.subtract(1, 'month');
          startDate = prevMonth.startOf('month');
          endDate = prevMonth.endOf('month');
          break;
        }
        case 'ytd': {
          startDate = now.startOf('year');
          endDate = now;
          break;
        }
        default: {
          // Unknown -> today
          startDate = now;
          endDate = now;
        }
      }

      return [startDate.toISO8601String(), endDate.toISO8601String()];
    },
    []
  );

  // Convert DataTable filter change to FilterChangeEvent
  const handleDataTableFilterChange = useCallback(
    (activeFilters: Record<string, string | string[]>) => {
      if (!onFilterChange) return;

      // Get all filter keys (both old and new)
      const allFilterKeys = new Set([
        ...Object.keys(activeFiltersForDataTable),
        ...Object.keys(activeFilters),
      ]);

      // Process each filter to detect changes
      allFilterKeys.forEach((key) => {
        const oldValue = activeFiltersForDataTable[key];
        const newValue = activeFilters[key];

        // If values are the same, skip
        if (
          oldValue === newValue ||
          (Array.isArray(oldValue) &&
            Array.isArray(newValue) &&
            oldValue.length === newValue.length &&
            oldValue.every((v) => newValue.includes(v)))
        ) {
          return;
        }

        // Handle period/date filter specially
        if (key === 'date') {
          if (!newValue) {
            // Period was cleared
            onFilterClear?.(SalesTaxFiltersType.PERIOD);
          } else if (
            typeof newValue === 'string' &&
            newValue.startsWith('custom:')
          ) {
            // Custom date range - extract dates
            const [, rangeStr] = newValue.split(':');
            const [fromDate, toDate] = rangeStr.split(',');
            // Ensure dates are properly formatted (ISO8601 strings)
            if (fromDate && toDate) {
              const trimmedFrom = fromDate.trim();
              const trimmedTo = toDate.trim();

              // Validate that fromDate <= toDate before sending to API
              // Extract just the date part (YYYY-MM-DD) for comparison
              const fromDateOnly = trimmedFrom.split('T')[0];
              const toDateOnly = trimmedTo.split('T')[0];

              if (fromDateOnly > toDateOnly) {
                // Invalid range - don't send to API
                // The DateRangePicker should have prevented this, but add extra safety
                console.warn(
                  'Invalid date range detected: fromDate > toDate. Filter not applied.',
                  { fromDate: trimmedFrom, toDate: trimmedTo }
                );
                return;
              }

              onFilterChange({
                filterId: SalesTaxFiltersType.PERIOD,
                value: [trimmedFrom, trimmedTo],
              });
            }
          } else if (typeof newValue === 'string') {
            // DataTable preset period preset:<code>
            const code = newValue.startsWith('preset:')
              ? newValue.replace('preset:', '')
              : newValue;
            const [fromDate, toDate] = calculateDateRangeFromPreset(code);
            onFilterChange({
              filterId: SalesTaxFiltersType.PERIOD,
              value: [fromDate, toDate],
            });
          }
          return;
        }

        // Handle reporting date override filter specially
        if (key === 'reporting_date') {
          if (!newValue) {
            // Reporting date was cleared
            onFilterClear?.(SalesTaxFiltersType.REPORTING_PERIOD);
          } else if (
            typeof newValue === 'string' &&
            newValue.startsWith('custom:')
          ) {
            // Custom date range - extract dates
            const [, rangeStr] = newValue.split(':');
            const [fromDate, toDate] = rangeStr.split(',');
            // Ensure dates are properly formatted (ISO8601 strings)
            if (fromDate && toDate) {
              const trimmedFrom = fromDate.trim();
              const trimmedTo = toDate.trim();

              // Validate that fromDate <= toDate before sending to API
              // Extract just the date part (YYYY-MM-DD) for comparison
              const fromDateOnly = trimmedFrom.split('T')[0];
              const toDateOnly = trimmedTo.split('T')[0];

              if (fromDateOnly > toDateOnly) {
                // Invalid range - don't send to API
                // The DateRangePicker should have prevented this, but add extra safety
                console.warn(
                  'Invalid reporting date range detected: fromDate > toDate. Filter not applied.',
                  { fromDate: trimmedFrom, toDate: trimmedTo }
                );
                return;
              }

              onFilterChange({
                filterId: SalesTaxFiltersType.REPORTING_PERIOD,
                value: [trimmedFrom, trimmedTo],
              });
            }
          } else if (typeof newValue === 'string') {
            // DataTable preset period preset:<code>
            const code = newValue.startsWith('preset:')
              ? newValue.replace('preset:', '')
              : newValue;
            const [fromDate, toDate] = calculateDateRangeFromPreset(code);
            onFilterChange({
              filterId: SalesTaxFiltersType.REPORTING_PERIOD,
              value: [fromDate, toDate],
            });
          }
          return;
        }

        // Handle destination country filter - map to server filter ID
        if (key === SalesTaxFiltersType.DESTINATION_COUNTRIES) {
          const newCountries = newValue
            ? Array.isArray(newValue)
              ? newValue
              : [newValue]
            : [];

          // Whenever country filter changes, filter out states that don't belong to selected countries
          const currentDestinationStates =
            activeFiltersForDataTable[SalesTaxFiltersType.DESTINATION_STATES];
          const currentDestinationStatesArray = currentDestinationStates
            ? Array.isArray(currentDestinationStates)
              ? currentDestinationStates
              : [currentDestinationStates]
            : [];

          // Filter states to only include those that belong to selected countries
          // If no countries are selected, remove all states
          const filteredStates =
            newCountries.length > 0
              ? currentDestinationStatesArray.filter((state: string) => {
                  const stateCountry = state.split('_')[0];
                  return newCountries.includes(stateCountry);
                })
              : [];

          // Update state filter if states were removed
          if (filteredStates.length !== currentDestinationStatesArray.length) {
            if (filteredStates.length > 0) {
              onFilterChange({
                filterId: SalesTaxFiltersType.DESTINATION_STATES,
                value: filteredStates,
              });
            } else {
              onFilterClear?.(SalesTaxFiltersType.DESTINATION_STATES);
            }
          }

          // Update country filter
          if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
            onFilterClear?.(SalesTaxFiltersType.DESTINATION_COUNTRIES);
          } else {
            onFilterChange({
              filterId: SalesTaxFiltersType.DESTINATION_COUNTRIES,
              value: newValue,
            });
          }
          return;
        }

        // Handle destination state filter - map to server filter ID
        if (key === SalesTaxFiltersType.DESTINATION_STATES) {
          if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
            onFilterClear?.(SalesTaxFiltersType.DESTINATION_STATES);
          } else {
            onFilterChange({
              filterId: SalesTaxFiltersType.DESTINATION_STATES,
              value: newValue,
            });
          }
          return;
        }

        // Handle source platform filter - map to server filter ID
        if (key === SalesTaxFiltersType.SOURCE_PLATFORMS) {
          if (!newValue) {
            onFilterClear?.(SalesTaxFiltersType.SOURCE_PLATFORMS);
          } else {
            onFilterChange({
              filterId: SalesTaxFiltersType.SOURCE_PLATFORMS,
              value:
                typeof newValue === 'string'
                  ? newValue
                  : Array.isArray(newValue)
                    ? newValue[0]
                    : newValue,
            });
          }
          return;
        }

        // Handle status filter - map to server filter ID
        if (key === SalesTaxFiltersType.STATUSES) {
          if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
            onFilterClear?.(SalesTaxFiltersType.STATUSES);
          } else {
            onFilterChange({
              filterId: SalesTaxFiltersType.STATUSES,
              value: Array.isArray(newValue) ? newValue : [newValue],
            });
          }
          return;
        }

        // Handle type filter - map to server filter ID
        if (key === SalesTaxFiltersType.TYPE) {
          if (!newValue) {
            onFilterClear?.(SalesTaxFiltersType.TYPE);
          } else {
            onFilterChange({
              filterId: SalesTaxFiltersType.TYPE,
              value:
                typeof newValue === 'string'
                  ? newValue
                  : Array.isArray(newValue)
                    ? newValue[0]
                    : newValue,
            });
          }
          return;
        }

        // Handle marketplace filter - map to server filter ID
        if (key === SalesTaxFiltersType.MARKETPLACES) {
          if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
            onFilterClear?.(SalesTaxFiltersType.MARKETPLACES);
          } else {
            onFilterChange({
              filterId: SalesTaxFiltersType.MARKETPLACES,
              value: newValue,
            });
          }
          return;
        }

        // Handle other filters (shouldn't happen, but fallback)
        if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
          // Filter was cleared
          onFilterClear?.(key);
        } else {
          // Filter value changed
          onFilterChange({
            filterId: key,
            value: newValue,
          });
        }
      });
    },
    [
      onFilterChange,
      onFilterClear,
      activeFiltersForDataTable,
      calculateDateRangeFromPreset,
    ]
  );

  // Build filterConfig for DataTable
  const filterConfig = useMemo((): DataTableFilterConfig | undefined => {
    if (!filterOptions || (!onFilterChange && !onFilterClear && !onClearAll)) {
      return undefined;
    }

    const selectedOrAvailableCountries = filters.destination_countries
      ? filters.destination_countries
      : filterOptions.data.countries;

    // Get state options filtered by available countries
    const stateOptions = getAllStatesOptions(
      selectedOrAvailableCountries
    ).filter(
      (state) =>
        filterOptions.data.jurisdictions.includes(state.value) &&
        (selectedOrAvailableCountries?.length === 0 ||
          selectedOrAvailableCountries.includes(state.value.split('_')[0]))
    );

    // Filter source platforms based on available integrations
    const availableSourcePlatforms = [
      {
        value: MANUAL_UPLOAD_SOURCE_PLATFORM,
        label: 'Manual upload',
      },
      ...Object.values(RootFiIntegration)
        .filter((integration) =>
          filterOptions.data.source_platforms.includes(integration)
        )
        .map((integration) => ({
          value: integration,
          label:
            integration && TransactionSourcePlatformText[integration]
              ? TransactionSourcePlatformText[integration]
              : integration,
        })),
    ];

    const availableMarketplaces =
      filterOptions?.data?.marketplaces?.map((marketplace) => ({
        value: marketplace,
        label: MarketplaceNameMapping[marketplace]
          ? MarketplaceNameMapping[marketplace]
          : marketplace,
      })) || [];

    // Filter countries based on available countries
    const availableCountries = getAllCountryOptions()
      .filter((country) => filterOptions.data.countries.includes(country.code))
      .map((country) => ({
        value: country.code,
        label: country.name,
      }));

    // Get visible columns to determine which filters to show
    const visibleColumnsSet = new Set(visibleColumns);

    // Create a set of filter columnIds that should be shown based on visible columns
    const visibleFilterIds = new Set<string>();
    Object.entries(COLUMN_TO_FILTER_MAP).forEach(([columnId, filterId]) => {
      if (visibleColumnsSet.has(columnId)) {
        visibleFilterIds.add(filterId);
      }
    });

    // Always show destination countries filter (it doesn't have a direct column)
    visibleFilterIds.add(SalesTaxFiltersType.DESTINATION_COUNTRIES);

    // Build all filters
    const allFilters = [
      {
        columnId: 'date',
        type: 'DATE_RANGE' as const,
        icon: <Icons.Calendar className="size-4" />,
        label: 'Transaction date',
        options: DEFAULT_DATE_OPTIONS.map((opt) => ({
          label: opt.label,
          value: opt.value,
        })),
      },
      {
        columnId: 'reporting_date',
        type: 'DATE_RANGE' as const,
        icon: <Icons.Calendar className="size-4" />,
        label: 'Reporting date',
        options: DEFAULT_DATE_OPTIONS.map((opt) => ({
          label: opt.label,
          value: opt.value,
        })),
      },
      {
        columnId: SalesTaxFiltersType.TYPE,
        type: 'SINGLE_SELECT' as const,
        icon: <Icons.CoinsStacked03 className="size-4" />,
        label: 'Type',
        options: [
          { label: 'Sale', value: TransactionType.SALE },
          { label: 'Refund', value: TransactionType.REFUND },
          { label: 'Purchase', value: TransactionType.PURCHASE },
          { label: 'Purchase refund', value: TransactionType.PURCHASE_REFUND },
        ],
      },
      {
        columnId: SalesTaxFiltersType.DESTINATION_COUNTRIES,
        type: 'MULTI_SELECT' as const,
        icon: <Icons.Globe04 className="size-4" />,
        label: 'Country',
        options: availableCountries,
        showSearch: true,
        placeholder: 'Search countries',
      },
      {
        columnId: SalesTaxFiltersType.DESTINATION_STATES,
        type: 'MULTI_SELECT' as const,
        icon: <Icons.Map01 className="size-4" />,
        label: 'Jurisdiction',
        options: stateOptions,
        showSearch: true,
        placeholder: 'Search jurisdictions',
      },
      {
        columnId: SalesTaxFiltersType.SOURCE_PLATFORMS,
        type: 'SINGLE_SELECT' as const,
        icon: <Icons.Link01 className="size-4" />,
        label: 'Source platform',
        options: availableSourcePlatforms,
      },
      {
        columnId: SalesTaxFiltersType.STATUSES,
        type: 'MULTI_SELECT' as const,
        icon: <Icons.CheckCircle className="size-4" />,
        label: 'Status',
        options: Object.values(TransactionStatus).map((status) => ({
          value: status,
          label: SALES_TAX_STATUS_TO_TEXT[status],
        })),
      },
      {
        columnId: SalesTaxFiltersType.MARKETPLACES,
        type: 'MULTI_SELECT' as const,
        icon: <Icons.ShoppingBag01 className="size-4" />,
        label: 'Marketplace',
        options: availableMarketplaces,
      },
    ];

    // Filter to only show filters for visible columns
    const visibleFilters = allFilters.filter((filter) =>
      visibleFilterIds.has(filter.columnId)
    );

    return {
      filters: visibleFilters,
      activeFilters: activeFiltersForDataTable,
      onFilterChange: handleDataTableFilterChange,
    };
  }, [
    filterOptions,
    filters.destination_countries,
    activeFiltersForDataTable,
    handleDataTableFilterChange,
    visibleColumns,
  ]);

  const renderSelectionActionMenu = () => (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        <p className="txt-small-medium text-white">
          {transactionSelection.totalSelected} transactions selected
        </p>
        {!transactionSelection.isSelectAllActive &&
          transactionSelection.selectedItemIds.size === data.length && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => transactionSelection.handleSelectAll(true)}
            >
              Select all {totalTransactions} transactions
            </Button>
          )}

        <Button variant="secondary" size="sm" onClick={handleExportSelected}>
          Export selected
        </Button>

        {enableInternalTools && (
          <Button
            variant="secondary"
            size="sm"
            onClick={deleteModalVisibility.show}
          >
            Delete selected
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <SalesTaxSectionLayout>
      <DataTable
        tableContainerClassName={tableContainerClassName}
        tableWrapperClassName={tableWrapperClassName}
        columnGroups={columnGroups}
        data={transactionsData?.transactions || []}
        onRowClick={(row) => {
          if (
            isFeatureLaunched(FeatureLaunch.SALES_TAX_TRANSACTIONS_DETAIL_PAGE)
          ) {
            router.push(
              `/sales-tax/transaction/${row.id}?corporationId=${corporationId}`
            );
          }
        }}
        paginationConfig={{
          isEnabled: true,
          pageSize,
          type: 'CURSOR',
          hasNextPage: !!nextCursor,
          onNextPage: handleNextPage,
          onPreviousPage: handlePreviousPage,
          hasPreviousPage:
            !!currentCursor && currentCursor !== FIRST_PAGE_CURSOR,
        }}
        searchConfig={{
          isEnabled: true,
          placeholder: 'Search by ID, customer, email, invoice, or amount',
          searchValue: searchTerm,
          columnsToEnableSearchFor: [
            'invoiceNumber',
            'sourcePlatformId',
            'amount',
            'taxCollected',
            'total',
            'id',
            'name',
            'email',
          ],
          onSearch: (searchValue) => {
            handleSearch(searchValue);
          },
          onFilterData: (dt) => {
            return dt;
          },
        }}
        tableViewConfig={{
          isEnabled: true,
          defaultView: 'regular',
        }}
        tableSortConfig={{
          sortingState: sortState ? [sortState] : undefined,
          onSortingChange: (sorting) => {
            onSortChange(
              sorting[0]
                ? {
                    id: sorting[0].id,
                    desc: sorting[0].desc,
                  }
                : null
            );
          },
        }}
        columnSelectorConfig={{
          isEnabled: true,
          placeholder: 'Customise columns',
          searchText: 'Search columns',
          notFoundText: 'No columns found',
          selectAllText: 'Show all columns',
          defaultVisibleColumns: initialColumns,
          onColumnVisibilityChange: (newVisibleColumns) => {
            // Update state so filters can react to column visibility changes
            setVisibleColumns(newVisibleColumns);
            localStorage.setItem(
              SALES_TAX_TRANSACTIONS_TABLE_COLUMN_GROUP_STORAGE_KEY,
              JSON.stringify(newVisibleColumns)
            );
          },
        }}
        rowSelectionConfig={{
          isEnabled: true,
          shouldShowRowSelectActionMenu: true,
          selectionMode: 'multiple',
          isRowSelected: (row) => transactionSelection.isItemSelected(row),
          initialSelectedRows: visibleSelectedRows.map((row) => row.id),
          onSelectionChange: (rows) => {
            if (rows.length === data.length) {
              transactionSelection.handleSelectPage(data, true);
            } else if (rows.length === 0) {
              transactionSelection.handleSelectPage(data, false);
              if (
                transactionSelection.selectedItemIds.size === 0 &&
                !transactionSelection.isSelectAllActive
              ) {
                transactionSelection.resetSelection();
              }
            } else {
              transactionSelection.handleSelectPage(data, false);
              rows.forEach((row) =>
                transactionSelection.handleSelectItem(row, true)
              );
            }
          },
          getRowId: (row: any) => row.id,
          rowSelectActionMenu: renderSelectionActionMenu,
        }}
        emptyStateConfig={getEmptyStateConfig()}
        filterConfig={filterConfig}
      />

      <TransactionDeleteModal
        isOpen={deleteModalVisibility.visible}
        onClose={deleteModalVisibility.hide}
        totalTransactions={transactionSelection.totalSelected}
        onDelete={handleDeleteSelected}
      />
    </SalesTaxSectionLayout>
  );
};

export default TransactionsTableSection;
